import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRoleCheck } from '../../../shared/auth/authUtils';
import useUserRoles from '../../../shared/hooks/useUserRoles';
import { StorageKey } from '../../../shared/auth/DataService';
import {
  deleteShipmentDelta,
  deleteStockDelta,
  deleteVoyageDelta,
  getScenario,
  upsertVoyageDelta,
} from '../../../shared/api/scenarioApi';
import { useScenario } from '../../../shared/contexts/ScenarioContext';
import { useVesselSalesSearch } from '../hooks/useVesselSalesSearch';
import {
  getVoyageWithShipments as fetchVoyageWithShipments,
  updateVoyage as updateVoyageWithShipments,
} from '../services/VoyageRepository';
import {
  TAB_DATA_KEYS,
  useReportingFilters,
  useReportingCoordination,
  useVoyageData,
  useShipmentData,
  useStockData,
  useChartData,
} from '../store';
import {
  buildVoyageDeltaPatch,
  buildVoyageUpdateRequest,
} from '../utils/voyageRequestBuilders';
import ScenarioBanner from './sales/components/scenario/ScenarioBanner';
import ScenarioManagerModal from './sales/components/scenario/ScenarioManagerModal';
import ScenarioViewportRing from './sales/components/scenario/ScenarioViewportRing';
import FilterBar from './sales/layout/FilterBar';
import VesselSalesModals from './sales/layout/VesselSalesModals';
import VesselSalesTabPanel from './sales/layout/VesselSalesTabPanel';
import withDataProvider from './sales/withDataProvider';

// Maps VesselSalesTabPanel tab indices to the data bucket each one needs —
// tabs 4 (Health Monitor) and 5 (Staging Area) fetch their own data independently.
const TAB_DATA_KEY_BY_INDEX = {
  0: TAB_DATA_KEYS.CHARTS,
  1: TAB_DATA_KEYS.VOYAGE,
  2: TAB_DATA_KEYS.SHIP_VIEW,
  3: TAB_DATA_KEYS.STOCK,
};

const VesselSalesHomePage = () => {
  useRoleCheck('VesselSalesHomePage', true);

  const { isMarketUser, isShippingCompanyUser } = useUserRoles();
  const canAccessScenarioManager = !isMarketUser;

  const { filters, setFilters, markets } = useReportingFilters();
  const {
    fetchAllData,
    stagedCount,
    fetchStagedCount,
    setActiveTab,
    loadingTabKey,
  } = useReportingCoordination();
  const refreshAllData = fetchAllData;
  const { fetchVoyagesPage, setSelectedVoyageId, voyages } = useVoyageData();
  const { shipmentDetails, isShipmentDetailsLoading } = useShipmentData();
  const { stockData, isStockLoading } = useStockData();
  const {
    tableStatisticsData,
    tableQuarterlyData,
    tableMonthlyData,
    summaryData,
    mapWidgetData,
    barChartPortWidgetDataETA,
    barChartPortWidgetDataETD,
    barChartWidgetDataETD,
    barChartWidgetDataETA,
  } = useChartData();

  const { activeScenario, resetEditingTimer } = useScenario();
  const isScenarioViewOnly = !!activeScenario && activeScenario.mode === 'VIEW';

  const [isScenarioManagerOpen, setIsScenarioManagerOpen] = useState(false);
  const [hasVoyageDeltas, setHasVoyageDeltas] = useState(false);
  const [hasStockDeltas, setHasStockDeltas] = useState(false);

  const refreshDeltaCounts = useCallback(async (scenarioId) => {
    if (!scenarioId) {
      setHasVoyageDeltas(false);
      setHasStockDeltas(false);
      return;
    }
    try {
      const dto = await getScenario(scenarioId);
      setHasVoyageDeltas(
        (dto.voyageDeltas ?? []).length > 0 ||
          (dto.shipmentDeltas ?? []).length > 0
      );
      setHasStockDeltas((dto.stockDeltas ?? []).length > 0);
    } catch {
      setHasVoyageDeltas(false);
      setHasStockDeltas(false);
    }
  }, []);

  useEffect(() => {
    refreshDeltaCounts(activeScenario?.id ?? null);
  }, [activeScenario, refreshDeltaCounts]);

  const handleUndoVoyages = async () => {
    if (!activeScenario) return;
    const scenarioData = await getScenario(activeScenario.id);
    const voyageDeltas = scenarioData.voyageDeltas ?? [];
    const shipmentDeltas = scenarioData.shipmentDeltas ?? [];
    // Delete orphaned shipment deltas first (not linked to a voyage delta)
    const voyageDeltaIds = new Set(voyageDeltas.map((d) => d.id));
    const orphanedShipmentDeltas = shipmentDeltas.filter(
      (sd) =>
        !sd.scenarioVoyageDeltaId ||
        !voyageDeltaIds.has(sd.scenarioVoyageDeltaId)
    );
    await Promise.all(
      orphanedShipmentDeltas.map((d) =>
        deleteShipmentDelta(activeScenario.id, d.id)
      )
    );
    // Then delete voyage deltas (cascades to non-orphaned shipment deltas)
    await Promise.all(
      voyageDeltas.map((d) => deleteVoyageDelta(activeScenario.id, d.id))
    );
    resetEditingTimer();
    setSnackbarMessage('All voyage scenario changes undone');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    fetchAllData();
    refreshDeltaCounts(activeScenario.id);
  };

  const handleUndoStock = async () => {
    if (!activeScenario) return;
    const scenarioData = await getScenario(activeScenario.id);
    const deltas = scenarioData.stockDeltas ?? [];
    await Promise.all(
      deltas.map((d) => deleteStockDelta(activeScenario.id, d.id))
    );
    resetEditingTimer();
    setSnackbarMessage('All stock scenario changes undone');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    fetchAllData();
    refreshDeltaCounts(activeScenario.id);
  };

  const handleUndoAll = async () => {
    if (!activeScenario) return;
    const scenarioData = await getScenario(activeScenario.id);
    const voyageDeltas = scenarioData.voyageDeltas ?? [];
    const shipmentDeltas = scenarioData.shipmentDeltas ?? [];
    const stockDeltas = scenarioData.stockDeltas ?? [];
    // Delete orphaned shipment deltas first
    const voyageDeltaIds = new Set(voyageDeltas.map((d) => d.id));
    const orphanedShipmentDeltas = shipmentDeltas.filter(
      (sd) =>
        !sd.scenarioVoyageDeltaId ||
        !voyageDeltaIds.has(sd.scenarioVoyageDeltaId)
    );
    await Promise.all(
      orphanedShipmentDeltas.map((d) =>
        deleteShipmentDelta(activeScenario.id, d.id)
      )
    );
    // Then delete voyage deltas (cascades to remaining shipment deltas) + stock deltas
    await Promise.all([
      ...voyageDeltas.map((d) => deleteVoyageDelta(activeScenario.id, d.id)),
      ...stockDeltas.map((d) => deleteStockDelta(activeScenario.id, d.id)),
    ]);
    resetEditingTimer();
    setSnackbarMessage('All scenario changes undone');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    fetchAllData();
    refreshDeltaCounts(activeScenario.id);
  };

  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (activeScenario && tabValue === 5) {
      setTabValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenario]);

  // Tells the data provider which tab is visible so it can lazily fetch
  // (or refetch, if invalidated) only that tab's data bucket.
  useEffect(() => {
    setActiveTab(TAB_DATA_KEY_BY_INDEX[tabValue] ?? null);
  }, [tabValue, setActiveTab]);

  // Index of the tab currently showing a loading indicator, if any.
  const loadingTabIndex = useMemo(() => {
    if (!loadingTabKey) return null;
    const entry = Object.entries(TAB_DATA_KEY_BY_INDEX).find(
      ([, key]) => key === loadingTabKey
    );
    return entry ? Number(entry[0]) : null;
  }, [loadingTabKey]);

  const [isFilterBarCollapsed, setIsFilterBarCollapsed] = useState(false);

  const [vesselFormMode, setVesselFormMode] = useState('create');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editVoyageRow, setEditVoyageRow] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [isVoyageHistoryModalOpen, setIsVoyageHistoryModalOpen] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isShipmentHistoryModalOpen, setIsShipmentHistoryModalOpen] =
    useState(false);
  const [selectedShipmentRow, setSelectedShipmentRow] = useState(null);

  const [addShipVoyage, setAddShipVoyage] = useState(null);
  const [isAddShipModalOpen, setIsAddShipModalOpen] = useState(false);

  const [isDoubleConfirmModalOpen, setIsDoubleConfirmModalOpen] =
    useState(false);
  const [rowData, setRowData] = useState(null);
  const [loadingVoyageId, setLoadingVoyageId] = useState(null);

  const {
    quickFilterValues,
    searchRaw,
    searchInputRef,
    clearSearch,
    handleSearchChange,
    resetSearchForTab,
  } = useVesselSalesSearch({ fetchVoyagesPage, setFilters });

  const handleTabChange = (event, newValue) => {
    resetSearchForTab();
    setTabValue(newValue);
  };

  const handleFilterBarToggle = (collapsed) =>
    setIsFilterBarCollapsed(collapsed);

  const handleVoyageHistoryClick = (row) => {
    setSelectedRow(row);
    setIsVoyageHistoryModalOpen(true);
  };

  const handleShipmentHistoryClick = (row) => {
    setSelectedShipmentRow(row);
    setIsShipmentHistoryModalOpen(true);
  };

  const handleTableRowClick = (voyageId) => setSelectedVoyageId(voyageId);

  const handleAddShipClick = (voyage) => {
    setAddShipVoyage(voyage);
    setIsAddShipModalOpen(true);
  };

  const handleAddShipModalClose = () => {
    setIsAddShipModalOpen(false);
    setAddShipVoyage(null);
    refreshDeltaCounts(activeScenario?.id ?? null);
  };

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setVesselFormMode('create');
    setEditVoyageRow(null);
    refreshDeltaCounts(activeScenario?.id ?? null);
  }, [activeScenario, refreshDeltaCounts]);

  const onEditClicked = (voyageData) => {
    setEditVoyageRow(voyageData);
    setVesselFormMode('group-edit');
    setOpenEditDialog(true);
  };

  const handleCancelSchedule = async (editData) => {
    const voyageId = editData.id;
    setLoadingVoyageId(voyageId);
    try {
      if (activeScenario) {
        await upsertVoyageDelta(activeScenario.id, {
          voyageId,
          changeType: 'CANCELLED',
          patch: { ...buildVoyageDeltaPatch(editData), cancelled: true },
        });
        resetEditingTimer();
        setSnackbarMessage('Voyage cancelled in scenario');
        refreshDeltaCounts(activeScenario.id);
      } else {
        const emea = localStorage.getItem(StorageKey.userInfo_sub);
        const { requestData } = buildVoyageUpdateRequest({
          editData,
          emea,
          cancelled: true,
        });
        await updateVoyageWithShipments(voyageId, requestData);
        setSnackbarMessage('Voyage cancelled successfully');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsDoubleConfirmModalOpen(false);
      fetchAllData();
    } catch (error) {
      console.error('Error updating voyage and shipments:', error);
      setSnackbarMessage('Failed to cancel voyage');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingVoyageId(null);
    }
  };

  const onCancelVoyageClicked = async (data) => {
    setLoadingVoyageId(data.id);
    try {
      if (activeScenario) {
        // Scenario mode: skip double-confirm, post delta directly, fetch full voyage with scenario overlay applied
        const voyage = await fetchVoyageWithShipments(data.id, {
          scenarioId: activeScenario.id,
        });
        await upsertVoyageDelta(activeScenario.id, {
          voyageId: data.id,
          changeType: 'CANCELLED',
          patch: { ...buildVoyageDeltaPatch(voyage), cancelled: true },
        });
        resetEditingTimer();
        setSnackbarMessage('Voyage cancelled in scenario');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        refreshDeltaCounts(activeScenario.id);
        fetchAllData();
      } else {
        const response = await fetchVoyageWithShipments(data.id);
        setRowData(response);
        setIsDoubleConfirmModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching voyage for cancel:', error);
      setSnackbarMessage('Failed to load voyage data');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingVoyageId(null);
    }
  };

  const handleUnCancelSchedule = async (editData) => {
    const voyageId = editData.id;
    try {
      if (activeScenario) {
        const voyage = await fetchVoyageWithShipments(voyageId, {
          scenarioId: activeScenario.id,
        });
        await upsertVoyageDelta(activeScenario.id, {
          voyageId,
          changeType: 'MODIFIED',
          patch: { ...buildVoyageDeltaPatch(voyage), cancelled: false },
        });
        resetEditingTimer();
        setSnackbarMessage('Voyage restored in scenario');
        refreshDeltaCounts(activeScenario.id);
      } else {
        const emea = localStorage.getItem(StorageKey.userInfo_sub);
        const { requestData } = buildVoyageUpdateRequest({
          editData,
          emea,
          cancelled: false,
        });
        await updateVoyageWithShipments(voyageId, requestData);
        setSnackbarMessage('Voyage restored successfully');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchAllData();
    } catch (error) {
      console.error('Error restoring voyage:', error);
      setSnackbarMessage('Failed to restore voyage');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const onUnCancelVoyageClicked = async (data) => {
    setLoadingVoyageId(data.id);
    try {
      if (activeScenario) {
        // Scenario mode: fetch full voyage with scenario overlay, post complete delta to restore
        const voyage = await fetchVoyageWithShipments(data.id, {
          scenarioId: activeScenario.id,
        });
        await upsertVoyageDelta(activeScenario.id, {
          voyageId: data.id,
          changeType: 'MODIFIED',
          patch: { ...buildVoyageDeltaPatch(voyage), cancelled: false },
        });
        resetEditingTimer();
        setSnackbarMessage('Voyage restored in scenario');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        refreshDeltaCounts(activeScenario.id);
        fetchAllData();
      } else {
        const response = await fetchVoyageWithShipments(data.id);
        await handleUnCancelSchedule(response);
      }
    } catch (error) {
      console.error('Error fetching voyage for restore:', error);
      setSnackbarMessage('Failed to load voyage data');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingVoyageId(null);
    }
  };

  const hideFilterBar = tabValue === 4 || tabValue === 5;

  return (
    <>
      {canAccessScenarioManager && <ScenarioViewportRing />}
      {canAccessScenarioManager && (
        <ScenarioBanner
          onOpenManager={() => setIsScenarioManagerOpen(true)}
          onUndoVoyages={hasVoyageDeltas ? handleUndoVoyages : undefined}
          onUndoStock={hasStockDeltas ? handleUndoStock : undefined}
          onUndoAll={
            hasVoyageDeltas || hasStockDeltas ? handleUndoAll : undefined
          }
        />
      )}
      <VesselSalesModals
        snackbarOpen={snackbarOpen}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        onSnackbarClose={() => setSnackbarOpen(false)}
        openEditDialog={openEditDialog}
        vesselFormMode={vesselFormMode}
        editVoyageRow={editVoyageRow}
        onCloseEditDialog={handleCloseEditDialog}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarSeverity={setSnackbarSeverity}
        isDoubleConfirmModalOpen={isDoubleConfirmModalOpen}
        rowData={rowData}
        loadingVoyageId={loadingVoyageId}
        onDoubleConfirmClose={() => setIsDoubleConfirmModalOpen(false)}
        onDoubleConfirmConfirm={() => handleCancelSchedule(rowData)}
        isAddShipModalOpen={isAddShipModalOpen}
        addShipVoyage={addShipVoyage}
        onAddShipModalClose={handleAddShipModalClose}
        isVoyageHistoryModalOpen={isVoyageHistoryModalOpen}
        selectedRow={selectedRow}
        onVoyageHistoryClose={() => setIsVoyageHistoryModalOpen(false)}
        handleShipmentHistoryClick={handleShipmentHistoryClick}
        isShipmentHistoryModalOpen={isShipmentHistoryModalOpen}
        selectedShipmentRow={selectedShipmentRow}
        onShipmentHistoryClose={() => setIsShipmentHistoryModalOpen(false)}
        readOnly={isScenarioViewOnly}
      />
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '25vh',
          marginBottom: '24px',
          marginTop: activeScenario ? '48px' : 0,
        }}
      >
        <img
          src='/resources/amg_gt.jpg'
          alt='some fancy Mercedes-Benz'
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '8%',
            textAlign: 'left',
            color: '#FFFFFF',
          }}
        >
          <Typography variant={'h1'}>Vessel Journal</Typography>
        </div>
      </div>
      <Box sx={{ display: 'flex', marginBottom: '24px' }}>
        <Box
          sx={{
            opacity: hideFilterBar ? 0 : 1,
            transform: hideFilterBar ? 'translateX(-100%)' : 'translateX(0)',
            marginRight: hideFilterBar
              ? isFilterBarCollapsed
                ? '-50px'
                : '-220px'
              : 0,
            transition:
              'opacity 0.4s ease, transform 0.4s ease, margin-right 0.4s ease',
            pointerEvents: hideFilterBar ? 'none' : 'auto',
          }}
        >
          <FilterBar
            onToggle={handleFilterBarToggle}
            filters={filters}
            setFilters={setFilters}
            markets={markets}
            shippingCompanyVisibility={true}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            transition: 'width 0.4s ease',
            width: hideFilterBar
              ? '100%'
              : isFilterBarCollapsed
                ? 'calc(100% - 50px)'
                : 'calc(100% - 220px)',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
        >
          <Stack
            direction='row'
            justifyContent='flex-end'
            spacing={1}
            sx={{ mb: 1 }}
          >
            {canAccessScenarioManager && (
              <Button
                variant='contained'
                size='small'
                color='warning'
                startIcon={<ScienceOutlinedIcon />}
                onClick={() => setIsScenarioManagerOpen(true)}
              >
                Manage Scenarios
              </Button>
            )}
          </Stack>
          <VesselSalesTabPanel
            tabValue={tabValue}
            onTabChange={handleTabChange}
            loadingTabIndex={loadingTabIndex}
            stagedCount={stagedCount}
            filters={filters}
            summaryData={summaryData}
            mapWidgetData={mapWidgetData}
            barChartWidgetDataETD={barChartWidgetDataETD}
            barChartWidgetDataETA={barChartWidgetDataETA}
            barChartPortWidgetDataETD={barChartPortWidgetDataETD}
            barChartPortWidgetDataETA={barChartPortWidgetDataETA}
            tableMonthlyData={tableMonthlyData}
            tableQuarterlyData={tableQuarterlyData}
            tableStatisticsData={tableStatisticsData}
            shipmentDetails={shipmentDetails}
            voyages={voyages}
            stockData={stockData}
            isStockLoading={isStockLoading}
            isShipmentDetailsLoading={isShipmentDetailsLoading}
            loadingVoyageId={loadingVoyageId}
            quickFilterValues={quickFilterValues}
            searchInputRef={searchInputRef}
            searchRaw={searchRaw}
            onSearchChange={handleSearchChange}
            onClearSearch={clearSearch}
            onEditClicked={onEditClicked}
            onVoyageHistoryClick={handleVoyageHistoryClick}
            onCancelVoyageClicked={onCancelVoyageClicked}
            onUnCancelVoyageClicked={onUnCancelVoyageClicked}
            onTableRowClick={handleTableRowClick}
            onAddShipClick={handleAddShipClick}
            setSnackbarOpen={setSnackbarOpen}
            setSnackbarMessage={setSnackbarMessage}
            setSnackbarSeverity={setSnackbarSeverity}
            onStagingResolved={() => {
              fetchStagedCount();
              refreshAllData();
            }}
            onScenarioDeltaChange={() =>
              refreshDeltaCounts(activeScenario?.id ?? null)
            }
          />
        </Box>
      </Box>
      {canAccessScenarioManager && (
        <ScenarioManagerModal
          open={isScenarioManagerOpen}
          onClose={() => setIsScenarioManagerOpen(false)}
          readOnly={isShippingCompanyUser}
        />
      )}
    </>
  );
};

export default withDataProvider(VesselSalesHomePage);
